import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const PlayerContext = createContext(null);

const SAVED_KEY = "mi_saved_v1";
const QUEUE_KEY = "mi_queue_v1";
const RATE_KEY = "mi_rate_v1";
const RESUME_KEY = "mi_resume_v1";
const AUTOPLAY_KEY = "mi_autoplay_v1";

const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
  });
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [queueOpen, setQueueOpen] = useState(false);
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"); } catch { return []; }
  });
  const [rate, setRateState] = useState(() => {
    const r = parseFloat(localStorage.getItem(RATE_KEY) || "1");
    return RATES.includes(r) ? r : 1;
  });
  const [resumeMap, setResumeMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RESUME_KEY) || "{}"); } catch { return {}; }
  });
  const [autoplayNext, setAutoplayNextState] = useState(() => {
    return localStorage.getItem(AUTOPLAY_KEY) === "1";
  });
  const setAutoplayNext = useCallback((v) => {
    setAutoplayNextState(v);
    localStorage.setItem(AUTOPLAY_KEY, v ? "1" : "0");
  }, []);

  const current = currentIndex >= 0 ? queue[currentIndex] : null;

  useEffect(() => { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)); }, [queue]);
  useEffect(() => { localStorage.setItem(SAVED_KEY, JSON.stringify(saved)); }, [saved]);
  useEffect(() => { localStorage.setItem(RATE_KEY, String(rate)); }, [rate]);

  // save resume position throttled to every 5 seconds
  const lastResumeSave = useRef(0);
  const persistResume = useCallback((trackId, sec) => {
    if (!trackId) return;
    const now = Date.now();
    if (now - lastResumeSave.current < 5000) return;
    lastResumeSave.current = now;
    setResumeMap(prev => {
      const nm = { ...prev, [trackId]: Math.floor(sec) };
      localStorage.setItem(RESUME_KEY, JSON.stringify(nm));
      return nm;
    });
  }, []);

  const clearResume = useCallback((trackId) => {
    setResumeMap(prev => {
      if (!(trackId in prev)) return prev;
      const nm = { ...prev }; delete nm[trackId];
      localStorage.setItem(RESUME_KEY, JSON.stringify(nm));
      return nm;
    });
  }, []);

  const getResume = useCallback((trackId) => resumeMap[trackId] || 0, [resumeMap]);

  // instantiate audio once
  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.volume = volume;
    a.playbackRate = rate;
    audioRef.current = a;
    const onTime = () => {
      const t = a.currentTime || 0;
      setProgress(t);
      // persist resume position (throttled inside)
      const cur = queueRef.current[currentIndexRef.current];
      if (cur) persistResume(cur.id, t);
    };
    const onDur = () => setDuration(a.duration || 0);
    const onEnd = () => {
      // clear resume for the finished track
      const cur = queueRef.current[currentIndexRef.current];
      if (cur) clearResume(cur.id);
      // call latest next via ref (avoids stale closure so autoplay works)
      nextRef.current && nextRef.current();
    };
    const onErr = () => { toast.error("Playback error — skipping"); nextRef.current && nextRef.current(); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onErr);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onErr);
      a.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep refs in sync so audio event handlers read current values
  const queueRef = useRef(queue);
  const currentIndexRef = useRef(currentIndex);
  const autoplayNextRef = useRef(autoplayNext);
  const nextRef = useRef(null);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { autoplayNextRef.current = autoplayNext; }, [autoplayNext]);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = rate; }, [rate]);

  const setRate = useCallback((r) => {
    setRateState(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  }, []);

  const playIndex = useCallback((idx) => {
    if (!audioRef.current) return;
    if (idx < 0 || idx >= queue.length) return;
    setCurrentIndex(idx);
    const t = queue[idx];
    audioRef.current.src = t.url;
    audioRef.current.playbackRate = rate;
    const startAt = resumeMap[t.id] || 0;
    audioRef.current.play().then(() => {
      setIsPlaying(true);
      if (startAt > 3 && startAt < (audioRef.current.duration || 1e9) - 5) {
        audioRef.current.currentTime = startAt;
        toast(`Resumed from ${Math.floor(startAt / 60)}:${String(Math.floor(startAt % 60)).padStart(2, "0")}`);
      }
    }).catch(() => setIsPlaying(false));
  }, [queue, rate, resumeMap]);

  const play = useCallback(() => {
    if (!audioRef.current || currentIndex < 0) return;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
  }, [currentIndex]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => (isPlaying ? pause() : play()), [isPlaying, pause, play]);

  const next = useCallback(() => {
    if (currentIndex + 1 < queue.length) { playIndex(currentIndex + 1); return; }
    // queue exhausted — try autoplay from same series
    if (!autoplayNextRef.current) { setIsPlaying(false); return; }
    const cur = queueRef.current[currentIndexRef.current];
    if (!cur?.parent) { setIsPlaying(false); return; }
    // fetch next lecture in same series and enqueue+play
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/next/${encodeURIComponent(cur.parent)}`)
      .then(r => r.ok ? r.json() : null)
      .then(payload => {
        const nx = payload?.next;
        if (!nx?.tracks?.length) { setIsPlaying(false); return; }
        const t = nx.tracks[0];
        const nextTrack = {
          id: `${nx.id}::${t.name}`,
          title: nx.title,
          creator: nx.creator,
          thumbnail: nx.thumbnail,
          url: t.url,
          length: t.length,
          parent: nx.id,
          parentTitle: nx.title,
        };
        setQueue(q => {
          const nq = [...q, nextTrack];
          setTimeout(() => {
            if (audioRef.current) {
              setCurrentIndex(nq.length - 1);
              audioRef.current.src = nextTrack.url;
              audioRef.current.playbackRate = rate;
              audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            }
          }, 0);
          return nq;
        });
        toast(`Autoplay · ${nx.title}`);
      })
      .catch(() => setIsPlaying(false));
  }, [currentIndex, queue.length, playIndex, rate]);

  // keep nextRef pointing to the latest `next` so audio 'ended' event uses fresh closure
  useEffect(() => { nextRef.current = next; }, [next]);

  const prev = useCallback(() => {
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0; return;
    }
    if (currentIndex - 1 >= 0) playIndex(currentIndex - 1);
  }, [currentIndex, playIndex]);

  const seek = useCallback((seconds) => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, seconds);
  }, []);

  const skip = useCallback((delta) => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, (audioRef.current.currentTime || 0) + delta);
  }, []);

  const playTracks = useCallback((tracks, startIdx = 0) => {
    if (!tracks?.length) return;
    setQueue(tracks);
    setTimeout(() => {
      if (audioRef.current) {
        setCurrentIndex(startIdx);
        const t = tracks[startIdx];
        audioRef.current.src = t.url;
        audioRef.current.playbackRate = rate;
        const startAt = resumeMap[t.id] || 0;
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          if (startAt > 3) {
            audioRef.current.currentTime = startAt;
            toast(`Resumed from ${Math.floor(startAt / 60)}:${String(Math.floor(startAt % 60)).padStart(2, "0")}`);
          }
        }).catch(() => {});
      }
    }, 0);
  }, [rate, resumeMap]);

  const addToQueue = useCallback((track, opts = {}) => {
    setQueue(q => {
      const nq = [...q, track];
      if (currentIndex === -1) {
        setTimeout(() => {
          if (audioRef.current) {
            setCurrentIndex(nq.length - 1);
            audioRef.current.src = track.url;
            audioRef.current.playbackRate = rate;
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }, 0);
      }
      return nq;
    });
    if (!opts.silent) toast.success("Added to queue");
  }, [currentIndex, rate]);

  const queueMany = useCallback((tracks) => {
    if (!tracks?.length) return;
    setQueue(q => {
      const nq = [...q, ...tracks];
      if (currentIndex === -1) {
        setTimeout(() => {
          if (audioRef.current) {
            const startIdx = q.length;
            setCurrentIndex(startIdx);
            audioRef.current.src = tracks[0].url;
            audioRef.current.playbackRate = rate;
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }, 0);
      }
      return nq;
    });
    toast.success(`Added ${tracks.length} track${tracks.length !== 1 ? "s" : ""} to queue`);
  }, [currentIndex, rate]);

  const removeFromQueue = useCallback((idx) => {
    setQueue(q => {
      const nq = q.filter((_, i) => i !== idx);
      if (idx === currentIndex) {
        audioRef.current?.pause();
        setIsPlaying(false);
        setCurrentIndex(-1);
      } else if (idx < currentIndex) {
        setCurrentIndex(ci => ci - 1);
      }
      return nq;
    });
  }, [currentIndex]);

  const clearQueue = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
    setCurrentIndex(-1);
    setQueue([]);
  }, []);

  const toggleSaved = useCallback((item) => {
    setSaved(s => {
      const exists = s.find(x => x.id === item.id);
      if (exists) { toast("Removed from Saved"); return s.filter(x => x.id !== item.id); }
      toast.success("Saved");
      return [{ id: item.id, title: item.title, creator: item.creator, thumbnail: item.thumbnail, savedAt: Date.now() }, ...s];
    });
  }, []);

  const isSaved = useCallback((id) => !!saved.find(x => x.id === id), [saved]);

  const value = useMemo(() => ({
    queue, currentIndex, current, isPlaying, progress, duration, volume,
    queueOpen, setQueueOpen,
    saved, isSaved, toggleSaved,
    rate, setRate, RATES,
    autoplayNext, setAutoplayNext,
    getResume, clearResume, resumeMap,
    playIndex, playTracks, addToQueue, queueMany, removeFromQueue, clearQueue,
    play, pause, toggle, next, prev, seek, skip, setVolume,
  }), [queue, currentIndex, current, isPlaying, progress, duration, volume, queueOpen, saved,
      isSaved, toggleSaved, rate, setRate, autoplayNext, setAutoplayNext,
      getResume, clearResume, resumeMap,
      playIndex, playTracks, addToQueue, queueMany, removeFromQueue, clearQueue,
      play, pause, toggle, next, prev, seek, skip]);

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
};

export const fmtTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60); const r = Math.floor(s % 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  return `${m}:${String(r).padStart(2, "0")}`;
};
